import React, { useState, ChangeEvent, useEffect, useMemo } from 'react'
import {
    Box,
    Divider,
    FormGroup,
    InputLabel,
    MenuItem,
    Paper,
    Typography,
    makeStyles
} from '@material-ui/core'
import { createMuiTheme, ThemeProvider } from '@material-ui/core/styles'
import { connect } from 'react-redux'
import actionEditorSelectors from 'selectors/actionEditorSelectors'
import { css } from '@emotion/react'
import { AtlasIcon } from 'web-components/atlas-icon'
import InfoIcon from 'assets/icons/InfoIcon'
import {
    AtlasBlack,
    AtlasBlackNearVar,
    AtlasGreyLight,
    ColorBaseMulberryBlue,
    ColorBaseRichBlack,
    ColorEastBay,
    FontWeightLightBold,
    FontWeightRegular
} from 'assets/styles/variables'
import { AddActionDatePickerStyle } from './form/DateField'
import {
    AddActionEditActionButton,
    DatePicker,
    DialogActions,
    NewDialogTextField
} from 'components/shared/StyledComponents'
import { bindActionCreators } from 'redux'
import { getFilteredActions } from 'businesslayer/api/actions'
import { useCommitteesState } from 'components/contexts/committee-context'
import { getContacts } from 'businesslayer/networkrequests'
import { actions } from 'reducers/actionDashboardReducer'
import i18n from 'common/util/internationalization'
import { pendoTrack } from 'common/util/functions'
import { StyledAtlasButtonOld } from './ActionItemEditDialog/ActionItemEditDialog'
import { AtlasSelect } from 'components/atlas-material/select'
import { fontFamilyCss } from './MarkCompleteView/MarkCompleteView'
import { transientStorageManager } from 'businesslayer/minutesSessionStore'
import { useMinutesManager } from 'pages/minutes-manager-page/use-minutes-manager'
import { AtlasTooltip } from 'components/atlas-components/atlas-tooltip/Tooltip'
import MultipleSelect from 'components/atlas-material/multi-select'

interface ActionGridFilterProps {
    dateInputMask: string
    columns: any
    actions: any
    setPage: any
    setIsLoading: any
    setApplyedFilters: any
    committeeForWhichUserHasActionsData: any
    formDataDisplay: any
    setFormDataDisplay: any
    completeUnlinkedActions: boolean
}

const useStyles = makeStyles((theme) => ({
    root: {
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: theme.palette.background.paper,
        height: '100%'
    },
    filter: {
        padding: '16px',
        alignItems: 'center',
        height: '92px',
        justifyContent: 'center'
    },
    textFieldContainer: {
        display: 'flex',
        alignItems: 'flex-start',
        minHeight: 50,
        placeItems: 'center'
    },
    dateField: {
        width: '100%',
        '& > div': {
            width: '100%'
        },
        '& > div > div': {
            width: '100%'
        },
        '& > *': {
            width: '100% !important'
        },
        [theme.breakpoints.down('md')]: {
            paddingTop: '12px',
            paddingBottom: '12px',
            '& > *': {
                width: '100% !important',
                display: 'contents'
            }
        }
    },
    textField: {
        minHeight: 40,
        marginTop: theme.spacing(1),
        '& p': {
            marginLeft: 0,
            marginRight: 0
        }
    },
    inputLabel: {
        marginTop: theme.spacing(1.3),
        fontFamily: 'Source Sans Pro',
        fontSize: '13px',
        fontWeight: FontWeightRegular,
        lineHeight: '16px',
        textAlign: 'left',
        color: ColorBaseRichBlack
    },
    filterBody: {
        gap: '8px',
        display: 'flex',
        flexDirection: 'column',
        padding: '16px',
        overflow: 'auto'
    },
    formControl: {
        width: '100%',
        fontFamilyCss,
        fontSize: '22px',
        fontWeight: FontWeightLightBold,
        lineHeight: '26px'
    },
    groupedField: {
        width: '48%',
        marginRight: 'auto'
    },
    groupedField1: {
        width: '48%',
        marginLeft: 'auto'
    },
    filterBtns: {
        display: 'flex',
        gap: '8px',
        float: 'right'
    },
    TitleContainer: {
        display: 'flex',
        justifyContent: 'space-between',
        height: '36px',
        placeItems: 'center'
    },
    iconGap: {
        '& span': {
            gap: '8px'
        }
    },
    ItemTitle: {
        fontFamily: 'Source Sans Pro',
        fontSize: '13px',
        fontWeight: FontWeightRegular,
        lineHeight: '16px',
        color: ColorBaseRichBlack
    },
    titleWithTooltip: {
        display: 'flex',
        alignItems: 'center',
        gap: '4px'
    },
    tooltipIcon: {
        display: 'inline-flex',
        alignItems: 'center',
        cursor: 'pointer',
        '&:focus, &:active': {
            outline: `2px solid ${ColorBaseMulberryBlue}`
        }
    },
    IconCss: {
        marginRight: '10px',
        '& path': {
            fill: 'transparent',
            stroke: ColorBaseRichBlack,
            color: ColorBaseRichBlack
        }
    },
    ResetButtonCss: {
        border: `1px solid ${ColorEastBay}`,
        '@media (max-width: 1200px)': {
            width: '85px'
        }
    },
    minutesTitleHintLabel: {
        fontFamily: 'Source Sans Pro',
        fontSize: '13px',
        fontWeight: FontWeightRegular,
        lineHeight: '16px',
        color: '#949494'
    },
    filterSubheader: {
        fontFamily: 'Source Sans Pro',
        fontSize: '16px',
        fontWeight: FontWeightRegular,
        lineHeight: '20px',
        color: ColorBaseRichBlack
    },
    dateInputTitle: {
        fontFamily: 'Source Sans Pro',
        fontSize: '16px',
        fontWeight: FontWeightLightBold,
        lineHeight: '20px',
        color: ColorBaseRichBlack,
        marginTop: 0
    },
    dialogActions: {
        display: 'flex',
        justifyContent: 'space-between',
        '@media (max-width: 1200px)': {
            justifyContent: 'center'
        }
    },
    applyButton: {
        [theme.breakpoints.down('sm')]: {
            whiteSpace: 'nowrap'
        }
    }
}))

const tooltipTheme = createMuiTheme({
    overrides: {
        MuiTooltip: {
            tooltip: {
                fontFamily: 'Source Sans Pro'
            }
        }
    }
})

const ActionGridFilter: React.FC<ActionGridFilterProps> = ({
    dateInputMask,
    actions,
    setPage,
    setIsLoading,
    formDataDisplay,
    setFormDataDisplay,
    setApplyedFilters,
    committeeForWhichUserHasActionsData,
    completeUnlinkedActions
}) => {
    const dateTextMask = [/\d/, /\d/, '/', /\d/, /\d/, '/', /\d/, /\d/, /\d/, /\d/]
    const classes = useStyles()
    const [formData, setFormData] = useState<any>({})
    const { committees } = useCommitteesState()
    const [allContacts, setAllcontacts] = useState<any[]>([])
    const { canAddMinutes } = useMinutesManager()
    const [currentUserCommittees, setCurerentUserCommittees] = useState<any[]>([])
    const [selectedItems, setSelectedItems] = React.useState<string[]>([])

    /*
     * New (Unlinked Actions feature only): committees the user has actions in but
     * which are no longer visible to them (deleted in Boards or unassigned) —
     * i.e. everything in the has-actions set that isn't an active committee.
     * Only computed when the feature toggle is on; otherwise defaults to empty
     * and the dedicated dropdown that consumes it is not rendered.
     */
    const unlinkedCommittees = useMemo(() => {
        if (!completeUnlinkedActions) return []
        const activeIds = new Set(committees.map((c) => +c.id))
        return committeeForWhichUserHasActionsData.filter((c) => !activeIds.has(+c.id))
    }, [completeUnlinkedActions, committees, committeeForWhichUserHasActionsData])

    /*
     * Default "Committee" filter source:
     * - toggle OFF: unchanged — the full merged list (active + unlinked/deleted).
     * - toggle ON: active/visible committees only; the unlinked ones move to the
     *   dedicated "Unlinked actions by committee" filter.
     */
    const defaultCommittees = useMemo(() => {
        if (!completeUnlinkedActions) return currentUserCommittees
        const activeIds = new Set(committees.map((c) => +c.id))
        return currentUserCommittees.filter((c) => activeIds.has(+c.id))
    }, [completeUnlinkedActions, currentUserCommittees, committees])

    useEffect(() => {
        setFormData(formDataDisplay)
    }, [formDataDisplay])

    useEffect(() => {
        ;(async () => {
            setCurerentUserCommittees(
                mergeUniqueCommittee(committees, committeeForWhichUserHasActionsData)
            )
            const data = await getContacts()
            const users = { ...data.contacts }
            const currentUserDisplayName = transientStorageManager?.currentUser?.display_name
            const filtered = Object.keys(users)
                .map((key) => ({ ...users[key].attributes, id: key }))
                .filter((user) => user.displayName === currentUserDisplayName)
            canAddMinutes === true
                ? setAllcontacts(
                      Object.keys(users)
                          .map((key) => ({ ...users[key].attributes, id: key }))
                          .sort((a, b) => a.displayName.localeCompare(b.displayName))
                  )
                : setAllcontacts(filtered)
        })()
    }, [committees, canAddMinutes, committeeForWhichUserHasActionsData])

    const [error, setError] = useState('')
    const [isApplyDisabled, setIsApplyDisabled] = useState(false)
    const handleDateChange = (name, date) => {
        date.setHours(0, 0, 0, 0) // set time to 00:00:00
        if (
            (name === 'due_to_date' &&
                formData.due_from_date &&
                date &&
                date < formData.due_from_date) ||
            (name === 'due_from_date' &&
                formData.due_to_date &&
                date &&
                date > formData.due_to_date)
        ) {
            setError(i18n.t('INVALID_MINUTES_START_FINISH_DATES'))
            setIsApplyDisabled(true)
        } else {
            setError('')
            setIsApplyDisabled(false)
        }
        setFormData({ ...formData, [name]: date })
    }

    const clearFilters = () => {
        setSelectedItems([])
        setFormData({
            committee_ids: '',
            [FORM_KEY_UNLINKED_COMMITTEE_IDS]: '',
            search_title: '',
            [FORM_KEY_ACTION_STATUS]: [],
            due_from_date: '',
            due_to_date: '',
            assignee_ids: ''
        })
        setError('')
        setIsApplyDisabled(false)
    }

    // Map localized status values to English for backend API
    const mapStatusToEnglish = (statusArray: string[]) => {
        if (!statusArray || statusArray.length === 0) return []

        return statusArray.map((status) => {
            const lowerStatus = status.toLowerCase()
            const completedText = i18n.t('REVIEW_COMPLETED').toLowerCase()
            const overdueText = i18n.t('OVERDUE_ACTION_LIST_LABEL').toLowerCase()
            const pendingText = i18n.t('PENDING').toLowerCase()

            const statusMap = {
                [completedText]: 'completed',
                [overdueText]: 'overdue',
                [pendingText]: 'pending'
            }

            return statusMap[lowerStatus]
        })
    }

    const FORM_KEY_ACTION_STATUS = 'action_status'
    const FORM_KEY_UNLINKED_COMMITTEE_IDS = 'unlinked_committee_ids'
    const COMMITTEE_NONE_VALUE = 'none'

    const applyFilters = async () => {
        setIsLoading(true)

        const formDataForApi: Record<string, string> = {}
        const urlParams = new URLSearchParams()
        for (const key in formData) {
            const raw = formData[key]
            if (raw == null) continue
            const val =
                key === FORM_KEY_ACTION_STATUS && Array.isArray(raw)
                    ? mapStatusToEnglish(raw).join(',')
                    : typeof raw === 'string'
                    ? raw.trim()
                    : raw
            const shouldAppend = typeof val !== 'string' || val
            if (shouldAppend) {
                const strVal = typeof val === 'string' ? val : String(val)
                urlParams.append(key, strVal)
                formDataForApi[key] = strVal
            }
        }
        setPage(1)
        const actionsData = await getFilteredActions(urlParams)
        actions.loadActions(actionsData)
        // Store the formatted values with English keys for subsequent API calls
        setApplyedFilters(formDataForApi)
        setFormDataDisplay(formData)
        setIsLoading(false)
    }

    const renderItemTitle = (item, itemkey, customClass = '', tooltipKey = '') => (
        <Box key={itemkey} role="heading" aria-level={3} className={classes.TitleContainer}>
            <Box className={classes.titleWithTooltip}>
                <Typography
                    aria-label={i18n.t(item)}
                    className={`${classes.ItemTitle} ${customClass}`}>
                    {i18n.t(item)}
                </Typography>
                {tooltipKey && (
                    <ThemeProvider theme={tooltipTheme}>
                        <AtlasTooltip
                            title={i18n.t(tooltipKey) as string}
                            interactive
                            placement="top">
                            <span
                                className={classes.tooltipIcon}
                                role="img"
                                tabIndex={0}
                                aria-label={i18n.t(tooltipKey) as string}>
                                <InfoIcon size={16} />
                            </span>
                        </AtlasTooltip>
                    </ThemeProvider>
                )}
            </Box>
        </Box>
    )
    const MAXCOMMITTEENAME = 30
    const truncateCommitteeName = (title, limit = MAXCOMMITTEENAME) => {
        return title.length > limit ? title.slice(0, limit) + '...' : title
    }
    const handleInputChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target
        if (
            Array.isArray(value) &&
            (value.includes(i18n.t('SELECT_ALL_PLACEHOLDER')) ||
                value.includes(i18n.t('SELECT_ALL_PLACEHOLDER').toLowerCase()))
        ) {
            setFormData({ ...formData, [name]: [] })
        } else {
            setFormData({ ...formData, [name]: value })
        }
    }

    dateInputMask = transientStorageManager.isDateInputMaskUS ? 'MM/dd/yyyy' : 'dd/MM/yyyy'

    const mergeUniqueCommittee = (committeeList1, committeeList2) => {
        const mergedData = {}

        ;[...committeeList1, ...committeeList2].forEach((item) => {
            const existing = mergedData[item.id] || {}
            mergedData[item.id] = {
                ...existing,
                ...item,
                attributes: { ...existing.attributes, ...item.attributes }
            }
        })

        const mergedDataArray = Object.values(mergedData)

        return mergedDataArray
    }

    return (
        <Paper
            datatest-id="action-grid-filter"
            className={classes.root}
            elevation={4}
            role="region"
            aria-labelledby={i18n.t('FILTERS')}>
            <Box role="heading" aria-level={2} id={i18n.t('FILTERS')} className={classes.filter}>
                <Typography className={classes.formControl} aria-label={i18n.t('FILTERS')}>
                    {i18n.t('FILTERS')}
                </Typography>

                <Typography
                    className={classes.filterSubheader}
                    aria-label={i18n.t('FILTER_OPTIONS.FILTERS_SUBHEADER')}>
                    {i18n.t('FILTER_OPTIONS.FILTERS_SUBHEADER')}
                </Typography>
            </Box>
            <Divider />

            <Box className={classes.filterBody}>
                <>
                    {renderItemTitle('COMMITTEE', 'committee')}

                    <AtlasSelect
                        datatest-id="committee_dropdown_filter"
                        role="combobox"
                        placeholder={i18n.t('SELECT_ALL_PLACEHOLDER')}
                        aria-labelledby={i18n.t('SELECT_ALL_PLACEHOLDER')}
                        name="committee_ids"
                        data-analytics="MM-FilterCommittee"
                        select
                        value={formData.committee_ids}
                        variant="outlined"
                        size="small"
                        onChange={(e) => {
                            handleInputChange(e)
                            setIsApplyDisabled(false)
                            pendoTrack('MM-FilterCommittee', {
                                interaction: 'change',
                                source: 'action-dashboard-filter',
                                domEvent: 'select'
                            })
                        }}>
                        <MenuItem className="dropDownFocus" value="">
                            {i18n.t('SELECT_ALL_PLACEHOLDER')}
                        </MenuItem>
                        {defaultCommittees.map((committee) => (
                            <MenuItem
                                className="dropDownFocus"
                                key={committee.id}
                                value={committee.id}>
                                {committee?.name?.length > MAXCOMMITTEENAME ? (
                                    <AtlasTooltip title={committee?.name} placement="top-start">
                                        <div>{truncateCommitteeName(committee?.name)}</div>
                                    </AtlasTooltip>
                                ) : (
                                    <div>{committee?.name}</div>
                                )}
                            </MenuItem>
                        ))}
                        {completeUnlinkedActions && unlinkedCommittees.length > 0 && (
                            <MenuItem className="dropDownFocus" value={COMMITTEE_NONE_VALUE}>
                                <div>{i18n.t('COMMITTEE_NONE_OPTION')}</div>
                            </MenuItem>
                        )}
                    </AtlasSelect>
                </>

                {completeUnlinkedActions && unlinkedCommittees.length > 0 && (
                    <>
                        {renderItemTitle(
                            'UNLINKED_ACTIONS_BY_COMMITTEE',
                            'unlinked_committee',
                            '',
                            'UNLINKED_ACTIONS_BY_COMMITTEE_TOOLTIP'
                        )}

                        <AtlasSelect
                            datatest-id="unlinked_committee_dropdown_filter"
                            role="combobox"
                            placeholder={i18n.t('SELECT_ALL_PLACEHOLDER')}
                            aria-labelledby={i18n.t('SELECT_ALL_PLACEHOLDER')}
                            name={FORM_KEY_UNLINKED_COMMITTEE_IDS}
                            data-analytics="MM-FilterUnlinkedActionsByCommittee"
                            select
                            value={formData[FORM_KEY_UNLINKED_COMMITTEE_IDS]}
                            variant="outlined"
                            size="small"
                            onChange={(e) => {
                                handleInputChange(e)
                                setIsApplyDisabled(false)
                                pendoTrack('MM-FilterUnlinkedActionsByCommittee', {
                                    interaction: 'change',
                                    source: 'action-dashboard-filter',
                                    domEvent: 'select'
                                })
                            }}>
                            <MenuItem className="dropDownFocus" value="">
                                {i18n.t('SELECT_ALL_PLACEHOLDER')}
                            </MenuItem>
                            {unlinkedCommittees.map((committee) => (
                                <MenuItem
                                    className="dropDownFocus"
                                    key={committee.id}
                                    value={committee.id}>
                                    {committee?.name?.length > MAXCOMMITTEENAME ? (
                                        <AtlasTooltip title={committee?.name} placement="top-start">
                                            <div>{truncateCommitteeName(committee?.name)}</div>
                                        </AtlasTooltip>
                                    ) : (
                                        <div>{committee?.name}</div>
                                    )}
                                </MenuItem>
                            ))}
                            {committees.length > 0 && (
                                <MenuItem className="dropDownFocus" value={COMMITTEE_NONE_VALUE}>
                                    <div>{i18n.t('COMMITTEE_NONE_OPTION')}</div>
                                </MenuItem>
                            )}
                        </AtlasSelect>
                    </>
                )}

                <>
                    {renderItemTitle('FILTER_OPTIONS.MINUTES_DOCUMENT', 'text')}
                    <Typography
                        aria-label={i18n.t('HINT_TEXT_SEARCH')}
                        className={classes.minutesTitleHintLabel}>
                        {i18n.t('HINT_TEXT_SEARCH')}
                    </Typography>
                    <FormGroup row>
                        <NewDialogTextField
                            name="search_title"
                            data-analytics="MM-FilterTitle"
                            datatest-id="search_title"
                            aria-label={i18n.t('FILTER_OPTIONS.MINUTES_DOCUMENT')}
                            placeholder={i18n.t('MINUTES_DOCUMENT_TITLE_PLACEHOLDER')}
                            value={formData.search_title}
                            variant="outlined"
                            size="small"
                            onChange={(e) => {
                                handleInputChange(e)
                                pendoTrack('MM-FilterTitle', {
                                    interaction: 'change',
                                    source: 'action-dashboard-filter',
                                    domEvent: 'input'
                                })
                            }}
                            fullWidth></NewDialogTextField>
                    </FormGroup>
                </>

                <>
                    {renderItemTitle('FILTER_OPTIONS.ACTION_STATUS', 'status')}
                    <FormGroup
                        style={{ display: 'block' }}
                        row
                        role="group"
                        aria-labelledby={i18n.t('SELECT_ALL_PLACEHOLDER')}>
                        <MultipleSelect
                            data-testid="status_dropdown_filter"
                            placeholder={i18n.t('SELECT_ALL_PLACEHOLDER')}
                            ariaLabelledby={i18n.t('SELECT_ALL_PLACEHOLDER')}
                            data-analytics="MM-FilterStatus"
                            aria-haspopup="listbox"
                            role="combobox"
                            name={FORM_KEY_ACTION_STATUS}
                            selectedItems={selectedItems}
                            setSelectedItems={setSelectedItems}
                            value={formData[FORM_KEY_ACTION_STATUS]}
                            variant="outlined"
                            size="small"
                            onChange={(e) => {
                                handleInputChange(e)
                                pendoTrack('MM-FilterStatus', {
                                    interaction: 'change',
                                    source: 'action-dashboard-filter',
                                    domEvent: 'select'
                                })
                            }}
                            fullWidth
                            helperText=""></MultipleSelect>
                    </FormGroup>
                </>

                <>
                    {renderItemTitle('ASSIGNED_TO', 'assigned_to')}
                    <AtlasSelect
                        datatest-id="assigned_to_dropdown_filter"
                        role="combobox"
                        aria-labelledby={i18n.t('ASSIGNED_TO')}
                        data-analytics="MM-FilterAssignedTo"
                        aria-haspopup="listbox"
                        select
                        name="assignee_ids"
                        variant="outlined"
                        size="small"
                        disabled={!canAddMinutes}
                        fullWidth
                        value={formData.assignee_ids}
                        placeholder={
                            !canAddMinutes
                                ? allContacts[0]?.displayName
                                : i18n.t('SELECT_ALL_PLACEHOLDER')
                        }
                        onChange={handleInputChange}>
                        {canAddMinutes && (
                            <MenuItem className="dropDownFocus" value="">
                                {i18n.t('SELECT_ALL_PLACEHOLDER')}
                            </MenuItem>
                        )}
                        {Array.from(allContacts)?.map((user) => (
                            <MenuItem className="dropDownFocus" value={user.id} key={user.id}>
                                {user?.displayName?.length > MAXCOMMITTEENAME ? (
                                    <AtlasTooltip
                                        title={user.displayName ?? ''}
                                        placement="top-start">
                                        <div>{truncateCommitteeName(user.displayName ?? '')}</div>
                                    </AtlasTooltip>
                                ) : (
                                    <div
                                        style={{
                                            color: !canAddMinutes
                                                ? `${AtlasGreyLight}`
                                                : `${AtlasBlack}`
                                        }}>
                                        {truncateCommitteeName(user.displayName ?? '')}
                                    </div>
                                )}
                            </MenuItem>
                        ))}
                    </AtlasSelect>
                </>

                <>
                    {renderItemTitle('DUE_DATE', 'due_date', classes.dateInputTitle)}
                    <InputLabel className={classes.inputLabel} htmlFor="due_from_date">
                        {i18n.t('FROM')}
                    </InputLabel>
                    <DatePicker
                        datatest-id="due_date_from"
                        data-analytics="MM-FilterDueFromDate"
                        aria-describedby="due_from_date_helper"
                        role="datepicker"
                        className={css`
                            ${AddActionDatePickerStyle}
                        `.toString()}
                        keyboard
                        id="due_from_date"
                        variant="outlined"
                        disableOpenOnEnter
                        size="small"
                        format={dateInputMask}
                        placeholder={dateInputMask.toUpperCase()}
                        onChange={(date) => handleDateChange('due_from_date', date)}
                        mask={(value) => (value ? dateTextMask : [])}
                        keyboardIcon={<AtlasIcon name="calendar" color={AtlasBlackNearVar} />}
                        value={formData.due_from_date || null}
                        error={false}
                        helperText={''}
                    />

                    <InputLabel className={classes.inputLabel} htmlFor="due_to_date">
                        {i18n.t('SEND_INVITATION_TO')}
                    </InputLabel>

                    <DatePicker
                        datatest-id="due_date_to"
                        data-analytics="MM-FilterDueToDate"
                        aria-describedby="due_to_date_helper"
                        role="datepicker"
                        className={css`
                            ${AddActionDatePickerStyle}
                        `.toString()}
                        keyboard
                        id="due_to_date"
                        variant="outlined"
                        disableOpenOnEnter
                        size="small"
                        format={dateInputMask}
                        placeholder={dateInputMask.toUpperCase()}
                        onChange={(date) => handleDateChange('due_to_date', date)}
                        mask={(value) => (value ? dateTextMask : [])}
                        keyboardIcon={<AtlasIcon name="calendar" color={AtlasBlackNearVar} />}
                        value={formData.due_to_date || null}
                        error={false}
                        helperText={''}
                    />
                    {error && (
                        <Typography color="error" variant="body2">
                            {error}
                        </Typography>
                    )}
                </>
            </Box>
            <Divider />
            <DialogActions className={classes.dialogActions}>
                <Box>
                    <AddActionEditActionButton
                        aria-label={i18n.t('RESET_FILTER')}
                        datatest-id="clear-filters"
                        disableFocusRipple
                        disableRipple
                        variant="text"
                        color="primary"
                        size="small"
                        className={classes.ResetButtonCss}
                        onClick={clearFilters}>
                        {i18n.t('RESET_FILTER')}
                    </AddActionEditActionButton>
                </Box>
                <Box>
                    <StyledAtlasButtonOld
                        aria-label={i18n.t('APPLY')}
                        datatest-id="apply-filters"
                        variant="contained"
                        disabled={isApplyDisabled}
                        color="primary"
                        style={{
                            border: isApplyDisabled ? 'transparent' : `1px solid ${ColorEastBay}`
                        }}
                        className={`${classes.applyButton} custom-class`}
                        onClick={applyFilters}>
                        {i18n.t('APPLY')}
                    </StyledAtlasButtonOld>
                </Box>
            </DialogActions>
        </Paper>
    )
}

const mapStateToProps = (state: any) => {
    return {
        dateInputMask: actionEditorSelectors.dateInputMask(state.actionEditorReducer)
    }
}
const mapDispatchToProps = (dispatch) => {
    const { loadActions } = actions

    return {
        actions: bindActionCreators({ loadActions }, dispatch)
    }
}

export default connect(mapStateToProps, mapDispatchToProps)(ActionGridFilter)
